<template>
  <div id="transactions" class="page-shell">
    <el-breadcrumb separator-class="el-icon-arrow-right" class="page-breadcrumb">
      <el-breadcrumb-item :to="{ name: 'Dashboard' }">Dashboard</el-breadcrumb-item>
      <el-breadcrumb-item>Transactions</el-breadcrumb-item>
    </el-breadcrumb>

    <div class="page-header transactions-page__header">
      <h1 class="page-title">Transactions</h1>
      <div class="transactions-page__header-actions">
        <el-button plain icon="el-icon-refresh" @click="refreshTransactions">Refresh</el-button>
      </div>
    </div>

    <section class="panel-card transactions-page__card">
      <div class="transactions-page__filters">
        <el-select v-model="draftFilters.searchField" class="transactions-page__search-field" placeholder="Field">
          <el-option
            v-for="option in searchFieldOptions"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          />
        </el-select>

        <el-input
          v-model.trim="draftFilters.keyword"
          class="transactions-page__search-input"
          placeholder="Search"
          clearable
          @keyup.enter.native="applyFilters"
        >
          <i slot="prefix" class="el-input__icon el-icon-search"></i>
        </el-input>

        <el-select v-model="draftFilters.status" class="transactions-page__status-select" clearable placeholder="All Status">
          <el-option v-for="option in statusOptions" :key="option" :label="option" :value="option" />
        </el-select>



        <el-date-picker
          v-model="draftFilters.dateRange"
          class="transactions-page__date-range"
          type="daterange"
          range-separator="To"
          start-placeholder="Start date"
          end-placeholder="End date"
          value-format="yyyy-MM-dd"
          unlink-panels
        />

        <el-button type="primary" icon="el-icon-search" class="transactions-page__search-button" @click="applyFilters">
          Search
        </el-button>
      </div>

      <div class="transactions-page__table-shell" v-loading="loading">
        <el-table
          :data="pagedTransactions"
          stripe
          border
          class="transactions-page__table"
          @sort-change="handleSortChange"
        >
          <el-table-column prop="dateValue" label="Date" sortable="custom" min-width="190">
            <template slot-scope="scope">
              <div class="transactions-page__date-cell">
                <i class="el-icon-time"></i>
                <span>{{ formatDate(scope.row.date) }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="direction" label="Direction" sortable="custom" min-width="120">
            <template slot-scope="scope">
              <span :class="getDirectionClass(scope.row.direction)" class="transactions-page__direction-pill">
                <i :class="getDirectionIcon(scope.row.direction)"></i>
                <span>{{ scope.row.direction }}</span>
              </span>
            </template>
          </el-table-column>

          <el-table-column prop="senderId" label="Sender" sortable="custom" min-width="180" />

          <el-table-column prop="receiverId" label="Receiver" sortable="custom" min-width="180" />

          <el-table-column prop="filename" label="Filename" sortable="custom" min-width="220" show-overflow-tooltip>
            <template slot-scope="scope">
              <el-link
                v-if="scope.row.filename && scope.row.filename !== '-'"
                type="primary"
                :underline="false"
                class="transactions-page__file-link"
                @click.prevent="openPayloadDialog(scope.row)"
              >
                {{ scope.row.filename }}
              </el-link>
              <span v-else>-</span>
            </template>
          </el-table-column>

          <el-table-column prop="status" label="Status" sortable="custom" min-width="130">
            <template slot-scope="scope">
              <span :class="getStatusClass(scope.row.status)" class="transactions-page__status-pill">
                {{ scope.row.status }}
              </span>
            </template>
          </el-table-column>





          <el-table-column label="Action" min-width="140" fixed="right">
            <template slot-scope="scope">
              <div class="transactions-page__actions">
                <el-button plain size="mini" icon="el-icon-document" @click="showDetail(scope.row)">Detail</el-button>
              </div>
            </template>
          </el-table-column>

          <template slot="empty">
            <div class="transactions-page__empty">
              <i class="el-icon-document"></i>
              <h3>No transactions found</h3>
              <p>Try adjusting the filter, date range, or search keyword.</p>
              <el-button plain @click="resetFilters">Clear filters</el-button>
            </div>
          </template>
        </el-table>
      </div>

      <div class="transactions-page__footer">
        <div class="transactions-page__footer-total">Total {{ filteredTransactions.length }}</div>
        <el-pagination
          background
          :current-page.sync="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 25, 50, 100]"
          :total="filteredTransactions.length"
          layout="sizes, prev, pager, next, jumper"
          @size-change="handlePageSizeChange"
        />
      </div>
    </section>

    <el-dialog
      :visible.sync="payloadDialogVisible"
      width="min(920px, 92vw)"
      custom-class="transactions-page__payload-dialog"
      :close-on-click-modal="false"
      destroy-on-close
    >
      <template slot="title">
        <div class="transactions-page__payload-header">
          <div class="transactions-page__payload-title">Payload Preview</div>
          <div class="transactions-page__payload-subtitle">
            {{ activePayloadTransaction ? activePayloadTransaction.filename : 'Message payload' }}
          </div>
        </div>
      </template>

      <div v-loading="payloadLoading" class="transactions-page__payload-body">
        <el-alert
          v-if="payloadError"
          :title="payloadError"
          type="error"
          :closable="false"
          class="transactions-page__payload-alert"
        />

        <template v-else-if="payloadPreview">
          <div class="transactions-page__payload-meta">
            <span class="transactions-page__chip">
              <strong>Content Type:</strong> {{ payloadPreview.content_type || '-' }}
            </span>
            <span class="transactions-page__chip">
              <strong>Size:</strong> {{ formatPayloadSize(payloadPreview.size) }}
            </span>
            <span v-if="payloadPreview.truncated" class="transactions-page__chip transactions-page__chip--warning">
              Preview truncated
            </span>
          </div>

          <el-alert
            v-if="payloadPreview.message"
            :title="payloadPreview.message"
            type="warning"
            :closable="false"
            class="transactions-page__payload-alert"
          />

          <pre v-if="payloadPreview.value" class="transactions-page__payload-pre">{{ payloadPreview.value }}</pre>
          <div v-else-if="!payloadPreview.message" class="transactions-page__mini-empty">
            No preview content is available for this payload.
          </div>
        </template>
      </div>

      <span slot="footer" class="dialog-footer transactions-page__payload-footer">
        <el-button @click="payloadDialogVisible = false">Close</el-button>
        <el-button
          type="primary"
          :loading="activePayloadTransaction ? isDownloadingTransaction(activePayloadTransaction) : false"
          @click="downloadPayloadFromDialog"
        >
          Download
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import moment from 'moment'
import Utils from '../utils'

export default {
  name: 'transactions-admin',
  data() {
    return {
      loading: false,
      downloadingMessageIds: [],
      payloadDialogVisible: false,
      payloadLoading: false,
      payloadError: '',
      payloadPreview: null,
      activePayloadTransaction: null,
      transactions: [],
      currentPage: 1,
      pageSize: 10,
      sortBy: 'dateValue',
      sortOrder: 'descending',
      stateMap: {
        success: [
          'msg_send_start',
          'msg_receive_start',
          'mdn_send_start',
          'mdn_receive_start',
          'msg_sent_mdn_received_ok',
          'msg_rxd_mdn_sent_ok',
          'msg_rxd_mdn_not_requested_ok',
          'msg_sent_mdn_received_mic_mismatch',
        ],
        pending: [
          'msg_send_exception',
          'msg_receive_exception',
          'mdn_sending_exception',
          'mdn_receiving_exception',
          'msg_sent_await_async_mdn_response',
          'msg_sent_mdn_processing_error',
        ],
        failed: [
          'msg_send_fail',
          'msg_send_fail_resend_queued',
          'msg_receive_fail',
          'msg_rxd_asyn_mdn_send_fail_resend_queued',
          'mdn_asyn_receive_fail',
          'msg_rxd_mdn_sending_fail',
          'msg_receive_error_sending_mdn_error',
          'msg_sent_mdn_received_error',
        ],
      },
      searchFieldOptions: [
        { label: 'Receiver AS2 ID', value: 'receiverId' },
        { label: 'Sender AS2 ID', value: 'senderId' },
        { label: 'Message ID', value: 'messageId' },
        { label: 'File Name', value: 'filename' },
        { label: 'Date', value: 'date' },
        { label: 'Direction', value: 'direction' },
      ],
      statusOptions: ['Success', 'Failed', 'Pending'],
      draftFilters: {
        searchField: 'receiverId',
        keyword: '',
        status: '',
        dateRange: [],
      },
      filters: {
        searchField: 'receiverId',
        keyword: '',
        status: '',
        dateRange: [],
      },
    }
  },
  computed: {
    filteredTransactions() {
      return this.transactions.filter((transaction) => {
        const keyword = (this.filters.keyword || '').toLowerCase()
        const searchField = this.filters.searchField
        const fieldValue = this.getSearchableValue(transaction, searchField)
        const matchesKeyword = !keyword || fieldValue.indexOf(keyword) !== -1
        const matchesStatus = !this.filters.status || transaction.status === this.filters.status
        const matchesDateRange = this.isWithinDateRange(transaction.date)

        return matchesKeyword && matchesStatus && matchesDateRange
      })
    },
    sortedTransactions() {
      const items = this.filteredTransactions.slice()
      const direction = this.sortOrder === 'ascending' ? 1 : -1

      items.sort((left, right) => {
        const leftValue = this.getSortValue(left, this.sortBy)
        const rightValue = this.getSortValue(right, this.sortBy)

        if (leftValue === rightValue) return 0
        return leftValue > rightValue ? direction : -direction
      })

      return items
    },
    pagedTransactions() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.sortedTransactions.slice(start, start + this.pageSize)
    },
  },
  watch: {
    filteredTransactions() {
      const maxPage = Math.max(1, Math.ceil(this.filteredTransactions.length / this.pageSize))
      if (this.currentPage > maxPage) {
        this.currentPage = 1
      }
    },
  },
  mounted() {
    this.loadTransactions()
  },
  methods: {
    async loadTransactions() {
      this.loading = true
      try {
        const results = await Utils.Crud.getList('messages')
        const mappedTransactions = results.map((item, index) => this.mapTransaction(item, index))
        this.transactions = this.collapseRetryChains(mappedTransactions)
      } finally {
        this.loading = false
      }
    },
    async applyFilters() {
      this.loading = true
      try {
        await new Promise((resolve) => setTimeout(resolve, 250))
        this.filters = {
          searchField: this.draftFilters.searchField,
          keyword: this.draftFilters.keyword,
          status: this.draftFilters.status,
          dateRange: this.draftFilters.dateRange ? this.draftFilters.dateRange.slice() : [],
        }
        this.currentPage = 1
      } finally {
        this.loading = false
      }
    },
    async refreshTransactions() {
      await this.loadTransactions()
      await this.applyFilters()
    },
    resetFilters() {
      this.draftFilters = {
        searchField: 'receiverId',
        keyword: '',
        status: '',
        dateRange: [],
      }
      this.filters = {
        searchField: 'receiverId',
        keyword: '',
        status: '',
        dateRange: [],
      }
      this.currentPage = 1
    },
    handleSortChange({ prop, order }) {
      this.sortBy = prop || 'dateValue'
      this.sortOrder = order || 'descending'
    },
    handlePageSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
    },
    formatDate(value) {
      if (!value) return '-'
      const parsedDate = this.parseDate(value)
      return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    mapTransaction(item, index) {
      const date = this.getField(item, 'CREATE_DT')
      const state = this.getField(item, 'STATE')
      const direction = this.normalizeDirection(this.getField(item, 'DIRECTION'), state)
      const parsedDate = this.parseDate(date)

      return {
        key: index,
        id: this.getField(item, 'ID') || this.getField(item, 'MSG_ID') || 'TX-' + String(index + 1).padStart(4, '0'),
        date,
        dateValue: parsedDate.isValid() ? parsedDate.valueOf() : 0,
        direction,
        senderId: this.getField(item, 'SENDER_ID') || '-',
        receiverId: this.getField(item, 'RECEIVER_ID') || '-',
        filename: this.getField(item, 'FILE_NAME') || '-',
        status: this.normalizeStatus(state),
        stateCode: state,
        messageId: this.getField(item, 'MSG_ID') || '-',
        priorMessageId: this.getField(item, 'PRIOR_MSG_ID') || '',
        isResend: this.getField(item, 'IS_RESEND') === 'Y',
        resendCount: this.getField(item, 'RESEND_COUNT') || '0',
        workflowStatus: this.getField(item, 'STATUS') || '-',
        raw: item,
      }
    },
    collapseRetryChains(items) {
      const byMessageId = {}
      items.forEach((item) => {
        if (item.messageId && item.messageId !== '-') {
          byMessageId[item.messageId] = item
        }
      })

      const groups = new Map()
      items.forEach((item) => {
        const rootMessageId = this.resolveRootMessageId(item, byMessageId)
        if (!groups.has(rootMessageId)) {
          groups.set(rootMessageId, [])
        }
        groups.get(rootMessageId).push(item)
      })

      return Array.from(groups.values()).map((group) => this.buildVisibleTransaction(group))
    },
    resolveRootMessageId(item, byMessageId) {
      let current = item
      const visited = new Set()

      while (current && current.priorMessageId && byMessageId[current.priorMessageId] && !visited.has(current.priorMessageId)) {
        visited.add(current.priorMessageId)
        current = byMessageId[current.priorMessageId]
      }

      return current && current.messageId ? current.messageId : item.messageId
    },
    buildVisibleTransaction(group) {
      const orderedGroup = group.slice().sort((left, right) => left.dateValue - right.dateValue)
      const root = orderedGroup.find((item) => !item.priorMessageId) || orderedGroup[0]
      const latest = orderedGroup[orderedGroup.length - 1]

      return {
        ...root,
        status: this.normalizeStatus(latest.stateCode),
        latestMessageId: latest.messageId,
        downloadMessageId: latest.messageId || root.messageId,
        latestStateCode: latest.stateCode,
        retryCount: String(Math.max(0, orderedGroup.length - 1)),
        attempts: orderedGroup,
      }
    },
    getField(item, key) {
      const lowerKey = key.toLowerCase()
      const value = item[key]
      const lowerValue = item[lowerKey]

      if (value !== undefined && value !== null && value !== '') return value
      if (lowerValue !== undefined && lowerValue !== null && lowerValue !== '') return lowerValue
      return null
    },
    parseDate(value) {
      if (!value) return moment.invalid()

      const parsedDate = moment(
        value,
        ['YYYY-MM-DD HH:mm:ss.SSS', 'YYYY-MM-DD HH:mm:ss.SS', 'YYYY-MM-DD HH:mm:ss.S', 'YYYY-MM-DD HH:mm:ss', moment.ISO_8601],
        true,
      )

      return parsedDate.isValid() ? parsedDate : moment(value)
    },
    normalizeStatus(state) {
      if (this.stateMap.success.includes(state)) return 'Success'
      if (this.stateMap.pending.includes(state)) return 'Pending'
      if (this.stateMap.failed.includes(state)) return 'Failed'
      return 'Pending'
    },
    normalizeDirection(direction, state) {
      const normalizedDirection = String(direction || '').toUpperCase()
      if (normalizedDirection === 'RECEIVE' || normalizedDirection === 'RECEIVED' || normalizedDirection === 'INBOUND') {
        return 'Receive'
      }
      if (normalizedDirection === 'SEND' || normalizedDirection === 'SENT' || normalizedDirection === 'OUTBOUND') {
        return 'Send'
      }
      if (String(state || '').indexOf('receive') !== -1 || String(state || '').indexOf('rxd') !== -1) {
        return 'Receive'
      }
      return 'Send'
    },
    getStatusClass(status) {
      if (status === 'Success') return 'transactions-page__status-pill--success'
      if (status === 'Pending') return 'transactions-page__status-pill--warning'
      return 'transactions-page__status-pill--danger'
    },
    getDirectionClass(direction) {
      return direction === 'Receive'
        ? 'transactions-page__direction-pill--receive'
        : 'transactions-page__direction-pill--send'
    },
    getDirectionIcon(direction) {
      return direction === 'Receive' ? 'el-icon-bottom-left' : 'el-icon-top-right'
    },
    getSortValue(item, sortBy) {
      if (sortBy === 'dateValue') return item.dateValue
      return String(item[sortBy] == null ? '' : item[sortBy]).toLowerCase()
    },
    getSearchableValue(transaction, searchField) {
      if (searchField === 'date') {
        return this.formatDate(transaction.date).toLowerCase()
      }
      return String(transaction[searchField] == null ? '' : transaction[searchField]).toLowerCase()
    },
    isWithinDateRange(dateValue) {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) return true

      const start = moment(this.filters.dateRange[0]).startOf('day')
      const end = moment(this.filters.dateRange[1]).endOf('day')
      const target = moment(dateValue)

      if (!target.isValid()) return false

      return target.isBetween(start, end, null, '[]')
    },
    showDetail(transaction) {
      this.$router.push({
        name: 'MessageDetail',
        params: { id: transaction.key },
        query: { msgId: transaction.messageId, source: 'transactions' },
      })
    },
    formatPayloadSize(size) {
      const numericSize = Number(size)
      if (!numericSize) return '0 B'
      if (numericSize < 1024) return `${numericSize} B`
      if (numericSize < 1024 * 1024) return `${(numericSize / 1024).toFixed(1)} KB`
      return `${(numericSize / (1024 * 1024)).toFixed(1)} MB`
    },
    getDownloadMessageId(transaction) {
      return transaction.downloadMessageId || transaction.latestMessageId || transaction.messageId
    },
    isDownloadingTransaction(transaction) {
      const messageId = this.getDownloadMessageId(transaction)
      return !!messageId && this.downloadingMessageIds.includes(messageId)
    },
    async openPayloadDialog(transaction) {
      const messageId = this.getDownloadMessageId(transaction)
      if (!messageId || messageId === '-') {
        this.$message.error('No payload is associated with this transaction.')
        return
      }

      this.activePayloadTransaction = transaction
      this.payloadDialogVisible = true
      this.payloadLoading = true
      this.payloadError = ''
      this.payloadPreview = null

      try {
        this.payloadPreview = await Utils.Crud.getMessagePayload(messageId)
      } catch (error) {
        this.payloadError = error && error.message ? error.message : String(error)
      } finally {
        this.payloadLoading = false
      }
    },
    async downloadPayloadFromDialog() {
      if (!this.activePayloadTransaction) {
        return
      }
      await this.downloadTransactionFile(this.activePayloadTransaction)
    },
    async downloadTransactionFile(transaction) {
      const messageId = this.getDownloadMessageId(transaction)
      if (!messageId || messageId === '-') {
        this.$message.error('No downloadable file is associated with this transaction.')
        return
      }

      if (this.downloadingMessageIds.includes(messageId)) {
        return
      }

      this.downloadingMessageIds = this.downloadingMessageIds.concat(messageId)
      const notification = this.$notify({
        title: 'Downloading file',
        message: `${transaction.filename} is being prepared for download.`,
        type: 'info',
        duration: 0,
      })

      try {
        await Utils.Crud.downloadMessageFile(messageId, transaction.filename)
        if (notification && typeof notification.close === 'function') {
          notification.close()
        }
        this.$message.success(`Download started for ${transaction.filename}.`)
      } catch (error) {
        if (notification && typeof notification.close === 'function') {
          notification.close()
        }
        this.$message.error(error && error.message ? error.message : String(error))
      } finally {
        this.downloadingMessageIds = this.downloadingMessageIds.filter((id) => id !== messageId)
      }
    },
  },
}
</script>

<style scoped>
.transactions-page__header {
  margin-bottom: 18px;
}

.transactions-page__header-actions {
  display: flex;
  gap: 12px;
}

.transactions-page__card {
  padding: 20px;
}

.transactions-page__filters {
  display: grid;
  grid-template-columns: minmax(0, 180px) minmax(0, 1.2fr) minmax(0, 140px) minmax(0, 1.3fr) minmax(0, 108px);
  gap: 14px;
  align-items: center;
  margin-bottom: 18px;
}

.transactions-page__search-field,
.transactions-page__search-input,
.transactions-page__status-select,
.transactions-page__date-range {
  width: 100%;
  min-width: 0;
}

.transactions-page__search-button {
  width: 100%;
  min-width: 0;
}

.transactions-page__table-shell {
  border: 1px solid #ebeef5;
  border-radius: 14px;
  overflow: hidden;
  background: linear-gradient(180deg, #ffffff 0%, #fffaf6 100%);
}

.transactions-page__table {
  width: 100%;
}

.transactions-page__date-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #606266;
}

.transactions-page__status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  padding: 7px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}

.transactions-page__status-pill--success {
  background: #67c23a;
  color: #fff;
}

.transactions-page__status-pill--warning {
  background: #e6a23c;
  color: #fff;
}

.transactions-page__status-pill--danger {
  background: #f56c6c;
  color: #fff;
}

.transactions-page__direction-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 96px;
  padding: 7px 12px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1;
}

.transactions-page__direction-pill i {
  font-size: 12px;
  font-weight: 700;
}

.transactions-page__direction-pill--receive {
  background: linear-gradient(135deg, #eff8ff 0%, #dbeafe 100%);
  border-color: #bfdbfe;
  color: #1d4ed8;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

.transactions-page__direction-pill--send {
  background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  border-color: #fed7aa;
  color: #c2410c;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

.transactions-page__file-link {
  max-width: 100%;
  font-weight: 600;
}

.transactions-page__error-text {
  color: #b42318;
}

.transactions-page__payload-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.transactions-page__payload-title {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}

.transactions-page__payload-subtitle {
  font-size: 13px;
  color: #6b7280;
}

.transactions-page__payload-body {
  min-height: 180px;
}

.transactions-page__payload-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}

.transactions-page__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
  font-size: 12px;
}

.transactions-page__chip--warning {
  background: #fff7ed;
  color: #c2410c;
}

.transactions-page__payload-alert {
  margin-bottom: 14px;
}

.transactions-page__payload-pre {
  margin: 0;
  padding: 16px;
  max-height: 60vh;
  overflow: auto;
  border-radius: 14px;
  background: #0f172a;
  color: #e2e8f0;
  font-size: 12px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}

.transactions-page__mini-empty {
  padding: 28px 16px;
  border: 1px dashed #d1d5db;
  border-radius: 14px;
  text-align: center;
  color: #6b7280;
}

.transactions-page__payload-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.transactions-page__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.transactions-page__empty {
  padding: 40px 16px;
  text-align: center;
  color: #909399;
}

.transactions-page__empty i {
  font-size: 32px;
  color: #c0c4cc;
}

.transactions-page__empty h3 {
  margin: 12px 0 8px;
  font-size: 18px;
  color: #303133;
}

.transactions-page__empty p {
  margin: 0 0 16px;
}

.transactions-page__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 18px;
  flex-wrap: wrap;
}

.transactions-page__footer-total {
  color: #606266;
  font-weight: 600;
}

@media (max-width: 1440px) {
  .transactions-page__filters {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 1024px) {
  .transactions-page__filters {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }

  .transactions-page__search-button {
    width: 100%;
  }
}

@media (max-width: 767px) {
  .transactions-page__card {
    padding: 16px;
    border-radius: 0;
  }

  .transactions-page__filters {
    grid-template-columns: 1fr;
  }

  .transactions-page__footer {
    align-items: stretch;
  }
}
</style>
